import { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../../components/feature/AdminLayout';
import {
  fetchAdminReleases,
  fetchAdminProducts,
  publishAdminRelease,
  unpublishAdminRelease,
  archiveAdminRelease,
  makeCurrentAdminRelease,
  deleteAdminRelease,
  type PlatformRelease,
  type PlatformProduct,
  type ReleaseStatus,
  type ReleaseType,
} from '../../api/releases.api';

const STATUS_LABELS: Record<ReleaseStatus, { label: string; badge: string }> = {
  published: { label: 'منشور', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  draft: { label: 'مسودة', badge: 'bg-slate-100 text-slate-700 border-slate-200' },
  scheduled: { label: 'مجدول', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  archived: { label: 'مؤرشف', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const TYPE_LABELS: Record<ReleaseType, { label: string; badge: string }> = {
  major: { label: 'رئيسي (Major)', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  minor: { label: 'فرعي (Minor)', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  patch: { label: 'تصحيح (Patch)', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  security: { label: 'أمني (Security)', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
};

export default function ReleasesListPage() {
  const navigate = useNavigate();
  const [releases, setReleases] = useState<PlatformRelease[]>([]);
  const [products, setProducts] = useState<PlatformProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Filters
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    const [prodRes, relRes] = await Promise.all([
      fetchAdminProducts(),
      fetchAdminReleases({
        page,
        per_page: 15,
        product_id: selectedProduct || undefined,
        status: selectedStatus || undefined,
        release_type: selectedType || undefined,
        search: search || undefined,
      }),
    ]);

    setLoading(false);

    if (prodRes.ok) {
      setProducts(prodRes.products);
    }

    if (relRes.ok) {
      setReleases(relRes.list.data);
      setTotalPages(relRes.list.last_page);
      setTotalCount(relRes.list.total);
    } else {
      if (relRes.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(relRes.message);
    }
  }, [navigate, page, selectedProduct, selectedStatus, selectedType, search]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleAction = async (release: PlatformRelease, action: 'publish' | 'unpublish' | 'archive' | 'make-current' | 'delete') => {
    setError('');
    setSuccessMsg('');

    if (action === 'delete') {
      if (!window.confirm(`هل أنت متأكد من حذف الإصدار ${release.version}؟ لا يمكن التراجع عن هذا الإجراء.`)) return;
      const res = await deleteAdminRelease(release.id);
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setSuccessMsg(`تم حذف الإصدار ${release.version} بنجاح.`);
      void loadData();
      return;
    }

    if (action === 'publish') {
      const res = await publishAdminRelease(release.id);
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setSuccessMsg(`تم نشر الإصدار ${release.version} وأصبح متاحاً للزوار على الموقع الآن.`);
      void loadData();
      return;
    }

    if (action === 'unpublish') {
      if (!window.confirm(`هل أنت متأكد من إلغاء نشر الإصدار ${release.version}؟ سيتم إخفاؤه من الموقع العام.`)) return;
      const res = await unpublishAdminRelease(release.id);
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setSuccessMsg(`تم إلغاء نشر الإصدار ${release.version} وإعادته كمسودة.`);
      void loadData();
      return;
    }

    if (action === 'archive') {
      if (!window.confirm(`هل أنت متأكد من أرشفة هذا الإصدار؟ لن يكون الإصدار الحالي وستتوقف التنزيلات النشطة.`)) return;
      const res = await archiveAdminRelease(release.id);
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setSuccessMsg(`تمت أرشفة الإصدار ${release.version}.`);
      void loadData();
      return;
    }

    if (action === 'make-current') {
      const res = await makeCurrentAdminRelease(release.id);
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setSuccessMsg(`تم اعتماد الإصدار ${release.version} كإصدار رئيسي حالي للمنتج ${release.product?.name || ''}.`);
      void loadData();
      return;
    }
  };

  // Quick stats
  const publishedCount = releases.filter(r => r.status === 'published').length;
  const draftCount = releases.filter(r => r.status === 'draft').length;
  const totalDownloads = releases.reduce((sum, r) => {
    const artSum = (r.artifacts || []).reduce((a, art) => a + (art.download_count || 0), 0);
    return sum + artSum;
  }, 0);

  return (
    <AdminLayout>
      <div className="space-y-6" dir="rtl">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl">
                <i className="ri-git-branch-line" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">إدارة إصدارات المنتجات (Release Management)</h1>
                <p className="text-sm text-gray-500">
                  إدارة شاملة لجميع منتجات DressnMore وإصداراتها وتحديثاتها وملفات التثبيت الرسمية
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to="/releases/products"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 font-medium text-sm transition-colors shadow-sm"
            >
              <i className="ri-apps-2-line text-blue-600" />
              إدارة المنتجات
            </Link>

            <a
              href="https://dressnmore.it.com/releases"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 font-medium text-sm transition-colors shadow-sm"
            >
              <i className="ri-external-link-line text-emerald-600" />
              عرض السجل على الموقع
            </a>

            <Link
              to="/releases/new"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors shadow-sm shadow-blue-200"
            >
              <i className="ri-add-line text-lg" />
              إصدار جديد
            </Link>
          </div>
        </div>

        {/* Notifications */}
        {error && (
          <div className="p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl flex items-center gap-3">
            <i className="ri-error-warning-line text-xl" />
            <span>{error}</span>
          </div>
        )}

        {successMsg && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl flex items-center gap-3">
            <i className="ri-checkbox-circle-line text-xl" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-2xl">
              <i className="ri-box-3-line" />
            </div>
            <div>
              <div className="text-2xl font-black text-gray-900">{totalCount}</div>
              <div className="text-xs text-gray-500 font-medium">إجمالي الإصدارات المسجلة</div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-2xl">
              <i className="ri-broadcast-line" />
            </div>
            <div>
              <div className="text-2xl font-black text-gray-900">{publishedCount}</div>
              <div className="text-xs text-gray-500 font-medium">إصدارات منشورة على الموقع</div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-2xl">
              <i className="ri-draft-line" />
            </div>
            <div>
              <div className="text-2xl font-black text-gray-900">{draftCount}</div>
              <div className="text-xs text-gray-500 font-medium">مسودات قيد الإعداد</div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center text-2xl">
              <i className="ri-download-cloud-2-line" />
            </div>
            <div>
              <div className="text-2xl font-black text-gray-900">{totalDownloads}</div>
              <div className="text-xs text-gray-500 font-medium">إجمالي تنزيلات نسخ الديسكتوب</div>
            </div>
          </div>
        </div>

        {/* Filters bar */}
        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-3 items-center w-full md:w-auto">
            {/* Search */}
            <div className="relative min-w-[220px]">
              <i className="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="بحث برقم الإصدار أو العنوان..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-3 pr-9 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Product filter */}
            <select
              value={selectedProduct}
              onChange={(e) => {
                setSelectedProduct(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white text-gray-700 focus:outline-none focus:border-blue-500"
            >
              <option value="">كل المنتجات</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            {/* Status filter */}
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white text-gray-700 focus:outline-none focus:border-blue-500"
            >
              <option value="">كل الحالات</option>
              <option value="published">منشور</option>
              <option value="draft">مسودة</option>
              <option value="scheduled">مجدول</option>
              <option value="archived">مؤرشف</option>
            </select>

            {/* Type filter */}
            <select
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white text-gray-700 focus:outline-none focus:border-blue-500"
            >
              <option value="">كل الأنواع</option>
              <option value="major">رئيسي (Major)</option>
              <option value="minor">فرعي (Minor)</option>
              <option value="patch">تصحيح (Patch)</option>
              <option value="security">أمني (Security)</option>
            </select>
          </div>

          {(search || selectedProduct || selectedStatus || selectedType) && (
            <button
              onClick={() => {
                setSearch('');
                setSelectedProduct('');
                setSelectedStatus('');
                setSelectedType('');
                setPage(1);
              }}
              className="text-xs text-rose-600 hover:text-rose-700 font-semibold flex items-center gap-1 shrink-0"
            >
              <i className="ri-close-circle-line" />
              إعادة ضبط الفلاتر
            </button>
          )}
        </div>

        {/* Releases Table / Cards */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <i className="ri-loader-4-line text-3xl animate-spin text-blue-600 mb-2 inline-block" />
              <p className="text-sm">جاري تحميل قائمة الإصدارات...</p>
            </div>
          ) : releases.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <i className="ri-folder-info-line text-4xl text-gray-300 mb-2 inline-block" />
              <p className="text-base font-semibold text-gray-700">لا توجد إصدارات مطابقة</p>
              <p className="text-sm text-gray-400 mt-1">ابدأ بإضافة أول إصدار للمنتج من زر "إصدار جديد".</p>
              <Link
                to="/releases/new"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium"
              >
                <i className="ri-add-line" />
                إضافة إصدار جديد
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-right text-sm">
                <thead>
                  <tr className="bg-gray-50/75 border-b border-gray-100 text-gray-600 text-xs font-semibold">
                    <th className="py-3.5 px-4">رقم الإصدار والنوع</th>
                    <th className="py-3.5 px-4">المنتج</th>
                    <th className="py-3.5 px-4">عنوان التحديث</th>
                    <th className="py-3.5 px-4">الحالة</th>
                    <th className="py-3.5 px-4">تاريخ الإصدار</th>
                    <th className="py-3.5 px-4">التغييرات</th>
                    <th className="py-3.5 px-4">ملفات التثبيت</th>
                    <th className="py-3.5 px-4 text-center">الإجراءات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {releases.map((release) => {
                    const statusInfo = STATUS_LABELS[release.status] || { label: release.status, badge: 'bg-gray-100' };
                    const typeInfo = TYPE_LABELS[release.release_type] || { label: release.release_type, badge: 'bg-gray-100' };
                    const changesCount = release.changes?.length || 0;
                    const artifactsCount = release.artifacts?.length || 0;

                    return (
                      <tr key={release.id} className="hover:bg-gray-50/50 transition-colors">
                        {/* Version & Badge */}
                        <td className="py-4 px-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900 text-base font-mono">
                              v{release.version}
                            </span>
                            {release.is_current && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-300">
                                <i className="ri-check-double-line" />
                                الحالي المعتمد
                              </span>
                            )}
                          </div>
                          <div className="mt-1">
                            <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-md border ${typeInfo.badge}`}>
                              {typeInfo.label}
                            </span>
                          </div>
                        </td>

                        {/* Product */}
                        <td className="py-4 px-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <i className="ri-box-3-line text-blue-600 text-base" />
                            <span className="font-medium text-gray-800">
                              {release.product?.name || 'غير محدد'}
                            </span>
                          </div>
                          <span className="text-xs text-gray-400 capitalize">
                            {release.product?.type || ''}
                          </span>
                        </td>

                        {/* Title & Summary */}
                        <td className="py-4 px-4 max-w-xs">
                          <div className="font-medium text-gray-900 truncate" title={release.title}>
                            {release.title}
                          </div>
                          {release.summary && (
                            <div className="text-xs text-gray-500 truncate mt-0.5" title={release.summary}>
                              {release.summary}
                            </div>
                          )}
                        </td>

                        {/* Status */}
                        <td className="py-4 px-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${statusInfo.badge}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            {statusInfo.label}
                          </span>
                          {release.status === 'scheduled' && release.scheduled_at && (
                            <div className="text-[11px] text-gray-400 mt-1">
                              {new Date(release.scheduled_at).toLocaleDateString('ar-EG')}
                            </div>
                          )}
                        </td>

                        {/* Release Date */}
                        <td className="py-4 px-4 whitespace-nowrap text-gray-600 text-xs">
                          {release.release_date || '-'}
                        </td>

                        {/* Changes Count */}
                        <td className="py-4 px-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-700 bg-gray-100 px-2.5 py-1 rounded-lg">
                            <i className="ri-list-check-3 text-gray-500" />
                            {changesCount} عنصر
                          </span>
                        </td>

                        {/* Artifacts */}
                        <td className="py-4 px-4 whitespace-nowrap">
                          {artifactsCount > 0 ? (
                            <div className="space-y-1">
                              {release.artifacts?.map((art) => (
                                <div key={art.id} className="flex items-center gap-1.5 text-xs text-gray-700">
                                  <i className="ri-file-download-line text-blue-600" />
                                  <span className="font-mono">{(art.file_size / (1024 * 1024)).toFixed(1)} MB</span>
                                  <span className="text-[11px] text-gray-400">({art.download_count} تنزيل)</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="py-4 px-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            {/* Preview */}
                            <Link
                              to={`/releases/${release.id}/preview`}
                              title="معاينة الإصدار"
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            >
                              <i className="ri-eye-line text-base" />
                            </Link>

                            {/* Edit */}
                            <Link
                              to={`/releases/${release.id}/edit`}
                              title="تعديل الإصدار"
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                            >
                              <i className="ri-edit-line text-base" />
                            </Link>

                            {/* Publish / Unpublish */}
                            {release.status === 'draft' || release.status === 'scheduled' ? (
                              <button
                                onClick={() => void handleAction(release, 'publish')}
                                title="نشر الإصدار على الموقع"
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-emerald-600 hover:bg-emerald-50 transition-colors"
                              >
                                <i className="ri-upload-cloud-2-line text-base" />
                              </button>
                            ) : release.status === 'published' ? (
                              <button
                                onClick={() => void handleAction(release, 'unpublish')}
                                title="إلغاء النشر (تحويل لمسودة)"
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-amber-600 hover:bg-amber-50 transition-colors"
                              >
                                <i className="ri-eye-off-line text-base" />
                              </button>
                            ) : null}

                            {/* Make Current (if published and not current) */}
                            {release.status === 'published' && !release.is_current && (
                              <button
                                onClick={() => void handleAction(release, 'make-current')}
                                title="اعتماد كإصدار حالي للمنتج"
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-indigo-600 hover:bg-indigo-50 transition-colors"
                              >
                                <i className="ri-star-line text-base" />
                              </button>
                            )}

                            {/* Archive (if not archived) */}
                            {release.status !== 'archived' && (
                              <button
                                onClick={() => void handleAction(release, 'archive')}
                                title="أرشفة الإصدار"
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                              >
                                <i className="ri-archive-line text-base" />
                              </button>
                            )}

                            {/* Delete (if draft) */}
                            {release.status === 'draft' && (
                              <button
                                onClick={() => void handleAction(release, 'delete')}
                                title="حذف المسودة"
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-rose-500 hover:bg-rose-50 transition-colors"
                              >
                                <i className="ri-delete-bin-line text-base" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                صفحة {page} من {totalPages} (إجمالي {totalCount} إصدار)
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs disabled:opacity-40 hover:bg-gray-50"
                >
                  السابق
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs disabled:opacity-40 hover:bg-gray-50"
                >
                  التالي
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
